import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Aside } from '@euler/components/Aside';
import { Avatar } from '@euler/components/Avatar';
import { Center } from '@euler/components/Center';
import { Colors, StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import Euler, { EulerViewProps } from '@euler/components/layout';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileLayout,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { VehicleSubscriptionSourceType } from '@euler/model/enum';
import { VehicleSubscriptionInfo } from '@euler/model/vehicle';
import { useServiceFactory } from '@euler/services/factory';
import {
  safeMarkDevFlag,
  showAlert,
  sleep,
  withConfirmation,
} from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { useActionSheet } from '@expo/react-native-action-sheet';
import clr from 'color';
import { memo, useCallback, useEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLimit = 50;

const kSimulateRemove = safeMarkDevFlag(false);

export const VehicleSubscriptionsScreen = wrapNavigatorScreen(
  ({ licensePlateNo }: { licensePlateNo: string }) => {
    const isMobile = useIsMobileLayout();
    if (isMobile) {
      return <Content licensePlateNo={licensePlateNo} />;
    }
    return (
      <Aside dismissible>
        <Content licensePlateNo={licensePlateNo} />
      </Aside>
    );
  },
  adaptiveWrapperOptions({
    title: '车辆关注记录',
    headerStatusBarHeight: undefined,
    headerShown: isMobileLayout(),
  }),
);

const Content = memo(({ licensePlateNo }: { licensePlateNo: string }) => {
  const loading = useAppLoading();
  const { bottom } = useSafeAreaInsets();
  const { showActionSheetWithOptions } = useActionSheet();
  const { vehicleInfoService } = useServiceFactory();

  const { data, status, fetch, fetchMore, update } =
    useInfiniteList<VehicleSubscriptionInfo>({
      limit: kLimit,
      query: async (offset: number) => {
        return await vehicleInfoService.getSubscriptions(
          { licensePlateNo, includeDetails: true },
          offset,
          kLimit,
        );
      },
    });

  const onPress = useCallback(
    (subscription: VehicleSubscriptionInfo) => {
      showActionSheetWithOptions(
        {
          cancelButtonIndex: 1,
          destructiveButtonIndex: 0,
          options: ['取消该关注', '关闭'],
        },
        async buttonIndex => {
          if (buttonIndex === 0) {
            if (
              !(await withConfirmation({
                title: Platform.OS === 'web' ? '' : '取消关注',
                message: '是否确认取消该关注? ',
              }))
            ) {
              return;
            }
            await loading.showWithAction(async () => {
              if (kSimulateRemove) {
                await sleep(3000);
              } else {
                await vehicleInfoService.removeSubscription(
                  subscription.subscriptionId,
                );
              }
            });
            await showAlert('已成功取消该关注!');
            update(x =>
              x.subscriptionId === subscription.subscriptionId
                ? 'remove'
                : undefined,
            );
          }
        },
      );
    },
    [loading, showActionSheetWithOptions, update, vehicleInfoService],
  );

  const renderItem: ListRenderItem<VehicleSubscriptionInfo> = useCallback(
    ({ item, index }) => {
      return (
        <Cell
          subscription={item}
          last={index === data.length - 1}
          onPress={onPress}
        />
      );
    },
    [data.length, onPress],
  );

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return (
    <FlatList
      data={data}
      keyExtractor={x => x.subscriptionId}
      initialNumToRender={kLimit}
      renderItem={renderItem}
      refreshing={status.isRefreshing}
      onRefresh={fetch}
      onEndReached={fetchMore}
      ListEmptyComponent={<EmptyView message="该车辆尚未关注" />}
      ListHeaderComponent={
        Platform.OS === 'web' && status.isLoading ? <ActivityIndicator /> : null
      }
      ListFooterComponent={
        status.loadedDataLength > 0 && status.isLoading ? (
          <ActivityIndicator />
        ) : null
      }
      refreshControl={
        Platform.OS !== 'web' ? (
          <RefreshControl refreshing={status.isRefreshing} onRefresh={fetch} />
        ) : undefined
      }
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: bottom,
      }}
    />
  );
});

const Cell = memo(
  ({
    last,
    subscription,
    onPress,
    ...props
  }: {
    last?: boolean;
    subscription: VehicleSubscriptionInfo;
    onPress?: (subscription: VehicleSubscriptionInfo) => void;
  } & EulerViewProps) => {
    const { rpx } = useResponsiveContext();
    const name = subscription.extra?.userInfo?.nickname || '未知';
    const avatar = subscription.extra?.userInfo?.headimgurl;
    const handlePress = useCallback(() => {
      onPress?.(subscription);
    }, [onPress, subscription]);
    return (
      <Euler.TouchableOpacity
        onPress={handlePress}
        backgroundColor="#fff"
        {...props}
      >
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={rpx(10)}
          paddingHorizontal={rpx(15)}
          paddingVertical={rpx(8)}
        >
          <Avatar
            uri={avatar}
            name={name}
            width={rpx(56)}
            height={rpx(56)}
            borderRadius={rpx(28)}
            overflow="hidden"
            textStyle={{
              fontFamily: FontFamily.NotoSans.Bold,
              fontSize: rpx(24),
              lineHeight: rpx(56),
              fontWeight: '700',
            }}
          />
          <StackLayout flex={1} alignItems="flex-start" gap={rpx(0)}>
            <Typography.Title level={3} mb={rpx(3)}>
              {name}
            </Typography.Title>
            <SourceTypeLabel type={subscription.source} />
            <Typography.Label
              medium
              size={rpx(10)}
            >{`${subscription.subscriptionId}`}</Typography.Label>
            <Typography.Label
              size={rpx(10)}
              light
              color="#777"
            >{`于${formatDateTime(
              subscription.createdAt,
            )}关注`}</Typography.Label>
          </StackLayout>
          <Typography.Label
            size={rpx(9)}
            color={Colors.Gray3}
          >{`${subscription.details.length}个相关服务`}</Typography.Label>
        </StackLayout>
        <Euler.View
          position="absolute"
          left={rpx(last ? 0 : 20)}
          right={0}
          bottom={0}
          height={StyleSheet.hairlineWidth}
          backgroundColor={'#ddd'}
        />
      </Euler.TouchableOpacity>
    );
  },
);

const kSourceTypeInfo: {
  [k in VehicleSubscriptionSourceType]: {
    text: string;
    color: string;
  };
} = {
  [VehicleSubscriptionSourceType.OfficialAccountQrcode]: {
    text: '扫公众号二维码',
    color: Colors.WeixinPrimary,
  },
  [VehicleSubscriptionSourceType.OrderServiceQrcode]: {
    text: '扫工单二维码',
    color: StatusColors.Info,
  },
  [VehicleSubscriptionSourceType.VehicleSubscription]: {
    text: '扫建单二维码',
    color: StatusColors.Success,
  },
  [VehicleSubscriptionSourceType.OpenApi]: {
    text: '其他方式',
    color: Colors.Green1,
  },
};

const SourceTypeLabel = memo(
  ({ type }: { type: VehicleSubscriptionSourceType }) => {
    const { rpx } = useResponsiveContext();
    const { text, color } =
      kSourceTypeInfo[type] ??
      kSourceTypeInfo[VehicleSubscriptionSourceType.OpenApi];
    return (
      <Center
        paddingHorizontal={rpx(8)}
        borderRadius={rpx(2)}
        backgroundColor={clr(color).alpha(0.15).toString()}
        borderWidth={StyleSheet.hairlineWidth}
        borderColor={color}
      >
        <Typography.Text size={rpx(8)} lineHeight={rpx(16)} color={color} light>
          {text}
        </Typography.Text>
      </Center>
    );
  },
);
